import "./style.css";
import { useEffect, useRef, useState } from "react";
import { ROUTES } from "@/app/routes";
import useRouteInfo from "@/app/hooks/useRouteInfo";
import { ICustomerProfileState } from "@/app_redux/reducers/slices/auth/customer_profile_slice";
import NoItemFoundComponent from "@/app/components/ui/NoItemFoundComponent";
import { useImageConstants } from "@/app/hooks/useImageConstants";
import { useNavigate } from "react-router";
import TableSkeletonComponent from "@/app/components/ui/LoaderComponent";
import { useLoader } from "@/app/hooks/useLoader";
import { useUtils } from "@/app/hooks/useUtils";
import { useVaccinationHistoryListAPI } from "@/app/hooks/api_hooks/Vaccination/useVaccinationHistoryListAPI";
import { useVaccinationSummaryListAPI } from "@/app/hooks/api_hooks/Vaccination/useVaccinationSummaryListAPI";
import { useModalActions } from "@/app/hooks/ui_hooks/useModalActions";
import RBACFlag from "@/app/components/core/RBACFlag";
import { useVaccinationReminderListAPI } from "@/app/hooks/api_hooks/Vaccination/useVaccinationReminderListAPI";
import OCRmodalComponent from "./OCRmodalComponent";
import { useVaccinationOCRAPI } from "@/app/hooks/api_hooks/Vaccination/useVaccinationOCRAPI";
import IteamDeleteComponent from "./IteamDeleteComponent";
import { AiOutlineEye } from "react-icons/ai";
import { MdDeleteForever, MdEdit } from "react-icons/md";
import FileViewComponent from "./FileViewComponent";
import { LuUpload } from "react-icons/lu";
import { IoMdAdd } from "react-icons/io";
import VaccinationDeworming from "./VaccinationDeworming";
import { XMarkIcon } from "@heroicons/react/24/outline";

const VaccinationTab = () => {
  const navigate = useNavigate();
  const fileInputRef = useRef(null);
  const { NO_CART } = useImageConstants();
  const { veticDateFormat } = useUtils();
  const modalRef = useRef<HTMLDivElement | null>(null);
  const { routeState } = useRouteInfo(ROUTES.ROOT.children.CUSTOMER_PROFILE.id);
  const { petDetails, customerId, petId, vaccinationData, vaccinationSummaryData } =
    routeState.state as ICustomerProfileState;
  const [page_step, setPageStep] = useState("vaccination_status");
  const { execute: fetchVaccinationHistoryList } = useVaccinationHistoryListAPI();
  const { execute: fetchVaccinationSummaryList } = useVaccinationSummaryListAPI();
  const { execute: fetchVaccinationReminderList } = useVaccinationReminderListAPI();
  const isVaccinationLoader = useLoader("isvaccinationTableLoading");
  const { setVisible } = useModalActions("VACCINATION_ENTER_DATA_MODAL");
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [itemDelete, setItemDelete] = useState(false);
  const [fileView, setFileView] = useState(false);
  const [fileData, setFileData] = useState(null);
  const [_isModalOpen, setIsModalOpen] = useState(null);
  const [vaccination_id, setVaccination_id] = useState(null);
  const [vaccination_file, setVaccination_file] = useState(null);
  const [mode, setMode] = useState(null);
  const [vaccinationErrorMessage, setVaccinationErrorMessage] = useState(null);
  const { execute: imageProcessingAPI } = useVaccinationOCRAPI();
  const [vaccinationProcessingData, setVaccinationProcessingData] = useState([]);
  const [showUploadPrompt, setShowUploadPrompt] = useState(false);

  useEffect(() => {
    if (petId && customerId) {
      if (page_step === "vaccination_history") {
        fetchVaccinationHistoryList(petId, customerId);
      } else if (page_step === "vaccination_status") {
        fetchVaccinationSummaryList(petId, customerId);
        fetchVaccinationReminderList(petId, customerId);
      }
    }
  }, [petId, customerId, page_step]);

  const handleEnterData = () => {
    setVisible();
  };

  const handleAddPetClick = () => {
    navigate(`/app/customer/profile/customer-details/add-pet/${customerId}`);
  };

  const handleUploadClick = () => {
    fileInputRef.current?.click();
  };

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (modalRef.current && !modalRef.current.contains(event.target as Node)) {
        setIsModalOpen(null);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [modalRef]);

  const handelClick = (source: "vaccination_status" | "vaccination_history" | "deworming") => {
    setPageStep(source);
    navigate(`/app/customer/profile/customer-details/vaccination/${customerId}/${petId}?document_source=${source}`);
  };

  useEffect(() => {
    const queryParams = new URLSearchParams(location.search);
    const source = queryParams.get("document_source");
    if (source === "vaccination_status") {
      setPageStep("vaccination_status");
    } else if (source === "deworming") {
      setPageStep("deworming");
    } else if (source === "vaccination_history") {
      setPageStep("vaccination_history");
    }
  }, [location.search]);

  useEffect(() => {
    const queryParams = new URLSearchParams(location.search);
    const isAddVaccine = queryParams.get("isAddVaccine");
    if (isAddVaccine) {
      setShowUploadPrompt(true);
    }
  }, [page_step]);

  return (
    <div className="pl-0 sm:pl-2 lg:pl-2">
      <div className="sm:flex sm:items-center ">
        <div className="sm:flex-auto">
          <h1 className="text-sm font-semibold text-gray-900 sm:mt-2 break-words">
            Vaccination Records For <span className="capitalize"> "{petDetails?.name}"</span>
          </h1>
        </div>
      </div>
      <div>
        {showUploadPrompt && (
          <div className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm flex items-center justify-center px-4 sm:px-6">
            <div className="bg-white rounded-2xl p-5 sm:p-8 max-w-2xl w-full animate-fadeIn ">
              <div className="flex justify-end">
                <XMarkIcon className="h-4 w-4 cursor-pointer" onClick={() => setShowUploadPrompt(false)} />
              </div>
              <div className="mb-6">
                <h2 className="text-xs font-semibold text-gray-800 text-center ">Upload Vaccination Data</h2>
                <p className="text-xs text-gray-500 mt-2 text-left">
                  Please upload your pet’s vaccination document to proceed.
                </p>
                <p className="text-xs text-gray-400 mt-1.5 text-left">
                  🧠 This upload is securely processed using AI to extract vaccination details automatically.
                </p>
                <p className="text-xs text-gray-400 mt-1.5 text-left">
                  📄 Supported formats: PDF, JPG, PNG (max 1MB each)
                </p>
              </div>
              <div className="mt-7 text-center">
                <button
                  onClick={() => {
                    handleUploadClick();
                    setShowUploadPrompt(false);
                  }}
                  className="inline-flex items-center justify-center gap-2 px-6 py-1.5 sm:py-2 bg-[var(--color-primary)] text-white text-xs font-medium rounded-lg hover:bg-gray-800 transition"
                >
                  <LuUpload className="w-4 h-4" />
                  Upload Document
                </button>
              </div>
            </div>
          </div>
        )}
        <div className="flex flex-col sm:flex-row justify-between sm:items-center mt-4 sm:mt-4 md:mt-3 lg:mt-3 mb-1 gap-3 sm:gap-4">
          <div className="flex flex-wrap justify-start w-full sm:w-auto gap-3 sm:gap-8">
            <div className="cursor-pointer" onClick={() => handelClick("vaccination_status")}>
              <div className="flex flex-col items-center">
                <p
                  className={`text-xs whitespace-nowrap ${
                    page_step === "vaccination_status"
                      ? "font-semibold text-violet-600"
                      : "font-normal text-muted-foreground"
                  }`}
                >
                  <div className="flex items-center gap-1">
                    <span>Vaccination Status</span>
                  </div>{" "}
                </p>
                <div
                  className={`h-[2px] rounded transition-all duration-500 ${
                    page_step === "vaccination_status" ? "bg-violet-600 w-full px-2" : "bg-[rgb(171,170,199)] w-0"
                  }`}
                  style={{ minWidth: "fit-content" }}
                ></div>
              </div>
            </div>
            <div className="cursor-pointer" onClick={() => handelClick("vaccination_history")}>
              <div className="flex flex-col items-center">
                <p
                  className={`text-xs whitespace-nowrap ${
                    page_step === "vaccination_history"
                      ? "font-semibold text-violet-600"
                      : "font-normal text-muted-foreground"
                  }`}
                >
                  <div className="flex items-center gap-1">
                    <span>Vaccination History</span>
                  </div>
                </p>
                <div
                  className={`h-[2px] rounded transition-all duration-500 ${
                    page_step === "vaccination_history" ? "bg-violet-600 w-full px-2" : "bg-[rgb(171,170,199)] w-0"
                  }`}
                  style={{ minWidth: "fit-content" }}
                ></div>
              </div>
            </div>
            <div className="cursor-pointer" onClick={() => handelClick("deworming")}>
              <div className="flex flex-col items-center">
                <p
                  className={`text-xs whitespace-nowrap ${
                    page_step === "deworming" ? "font-semibold text-violet-600" : "font-normal text-muted-foreground"
                  }`}
                >
                  <div className="flex items-center gap-1">
                    <span>Deworming</span>
                  </div>
                </p>
                <div
                  className={`h-[2px] rounded transition-all duration-500 ${
                    page_step === "deworming" ? "bg-violet-600 w-full px-2" : "bg-[rgb(171,170,199)] w-0"
                  }`}
                  style={{ minWidth: "fit-content" }}
                ></div>
              </div>
            </div>
          </div>
          <div className="flex flex-wrap items-center justify-end gap-2 sm:gap-4 w-full sm:w-auto self-end sm:self-auto mt-2 sm:mt-0 mb-2 sm:mb-0">
            <input
              type="file"
              multiple
              ref={fileInputRef}
              style={{ display: "none" }}
              onChange={(e) => {
                const files = e.target.files;
                if (files && files.length > 0) {
                  setOpen(true);
                  setLoading(true);
                  setMode("UPLOAD");
                  setFileData(files);
                  imageProcessingAPI(files, customerId, petId).then(({ status, message }) => {
                    if (status) {
                      setLoading(false);
                      setVaccinationProcessingData(message);
                      setVaccinationErrorMessage(null);
                    } else {
                      setLoading(false);
                      setVaccinationProcessingData([]);
                      setVaccinationErrorMessage(message);
                    }
                  });
                }
                e.target.value = "";
              }}
            />
            <RBACFlag actionId="VACCINATION_HISTORY_OCR_ACTION">
              <button
                type="button"
                className="py-1.5 sm:py-2 px-3 sm:px-4 rounded-lg bg-[var(--color-primary)] text-white text-xs font-medium hover:opacity-90 flex items-center gap-2 whitespace-nowrap"
                onClick={() => handleUploadClick()}
              >
                <LuUpload className="w-3.5 h-3.5" />
                <span className="hidden sm:inline">Upload New Document</span>
                <span className="sm:hidden">Upload</span>
              </button>
            </RBACFlag>
            <RBACFlag actionId="ENTER_DATA_ACTION_CTA">
              <button
                type="button"
                onClick={() => handleEnterData()}
                className="py-1.5 sm:py-2 px-3 sm:px-4 rounded-lg bg-[var(--color-primary)] text-white text-xs font-medium hover:opacity-90 flex items-center gap-1.5 whitespace-nowrap"
              >
                <IoMdAdd className="w-4 h-4" />
                <span>Enter Data</span>
              </button>
            </RBACFlag>
          </div>
        </div>
      </div>
      <div>
        {page_step === "vaccination_history" && (
          <div className="mt-3 pb-5 sm:pb-0">
            {isVaccinationLoader ? (
              <TableSkeletonComponent columnLength={5} bodyLength={6} />
            ) : (
              <div className="scroll-inner !h-auto max-h-[450px] 2xl:max-h-[550px] flow-root relative rounded-xl bg-[#F9FAFB]">
                <div className="w-full">
                  <div className="inline-block min-w-full align-middle">
                    {Array.isArray(vaccinationData) && vaccinationData.length > 0 ? (
                      <table className="w-full min-w-[680px] sm:min-w-[760px] md:min-w-[980px] border-separate border-spacing-x-0 border-spacing-y-0.5 bg-[#F5F5F5] rounded-xl px-[3px] pb-[1px]">
                        <thead className="bg-[#F5F5F5] text-[#1F1F23] font-normal">
                          <tr>
                            <th className="p-2 py-3 text-left text-xs whitespace-nowrap font-medium text-black tracking-wider">
                              Given Date
                            </th>
                            <th className="p-2 py-3 text-left text-xs whitespace-nowrap font-medium text-black tracking-wider">
                              Name
                            </th>
                            <th className="p-2 py-3 text-left text-xs whitespace-nowrap font-medium text-black tracking-wider">
                              Uploaded By
                            </th>
                            <th className="p-2 py-3 text-center text-xs whitespace-nowrap font-medium text-black tracking-wider">
                              Action
                            </th>
                          </tr>
                        </thead>
                        <tbody>
                          {vaccinationData?.map((vac, index) => {
                            const isFirst = index === 0;
                            const isLast = index === vaccinationData.length - 1;
                            const isSingle = vaccinationData.length === 1;
                            return (
                              <tr
                                key={index}
                                className={`
                                  bg-white
                                  ${isSingle ? "rounded-xl" : ""}
                                  ${isFirst && !isSingle ? "rounded-t-xl" : ""}
                                  ${isLast && !isSingle ? "rounded-b-xl" : ""}
                                `}
                              >
                                <td
                                  className={`
                                    whitespace-nowrap px-2 py-3 text-xs text-black
                                    ${isSingle ? "rounded-tl-2xl rounded-bl-2xl" : ""}
                                    ${isFirst && !isSingle ? "rounded-tl-2xl" : ""}
                                    ${isLast && !isSingle ? "rounded-bl-2xl" : ""}
                                  `}
                                >
                                  {veticDateFormat(vac?.given_date) || "--"}
                                </td>
                                <td className="whitespace-nowrap px-2 py-3 text-xs font-medium text-black">
                                  {vac?.vaccine_name}
                                </td>
                                <td className="whitespace-nowrap px-2 py-3 text-xs text-black">
                                  <div className="flex flex-col gap-1">
                                    <span>{vac?.created_by}</span>
                                  </div>
                                  <p className="text-xs">{veticDateFormat(vac?.created_at) || "NA"}</p>
                                </td>
                                <td
                                  className={`
                                    whitespace-nowrap px-2 py-3 text-center text-xs text-black
                                    ${isSingle ? "rounded-tr-2xl rounded-br-2xl" : ""}
                                    ${isFirst && !isSingle ? "rounded-tr-2xl" : ""}
                                    ${isLast && !isSingle ? "rounded-br-2xl" : ""}
                                  `}
                                >
                                  {vac?.file_url ? (
                                    <div className="flex gap-7 justify-center">
                                      {vac?.is_deletable ? (
                                        <div
                                          onClick={() => {
                                            setOpen(true);
                                            setVaccination_id(vac?.vaccination_id);
                                            setVaccination_file(vac?.file_url);
                                            setMode("EDIT");
                                          }}
                                          className="flex items-center gap-2 cursor-pointer hover:text-blue-600"
                                        >
                                          <MdEdit className="w-5 h-5" />
                                        </div>
                                      ) : (
                                        <div className="flex items-center gap-2 cursor-pointer hover:text-blue-600">
                                          -
                                        </div>
                                      )}
                                      <div
                                        onClick={() => {
                                          setFileView(true);
                                          setVaccination_file(vac?.file_url);
                                        }}
                                        className="flex items-center gap-2 cursor-pointer hover:text-blue-600"
                                      >
                                        <AiOutlineEye className="w-5 h-5" />
                                      </div>
                                      {vac?.is_deletable ? (
                                        <div
                                          onClick={() => {
                                            setItemDelete(true);
                                            setVaccination_id(vac?.vaccination_id);
                                            setVaccination_file(vac?.file_url);
                                          }}
                                          className="flex items-center gap-2 cursor-pointer hover:text-red-600"
                                        >
                                          <MdDeleteForever color="gray" className="w-5 h-5" />
                                        </div>
                                      ) : (
                                        <div className="flex items-center gap-2 cursor-pointer hover:text-blue-600">
                                          -
                                        </div>
                                      )}
                                    </div>
                                  ) : (
                                    <div className="flex gap-10 justify-center">
                                      <div className="flex items-center gap-2 cursor-pointer hover:text-blue-600">
                                        -
                                      </div>
                                      <div className="flex items-center gap-2 cursor-pointer hover:text-blue-600">
                                        -
                                      </div>
                                      <div className="flex items-center gap-2 cursor-pointer hover:text-red-600">-</div>
                                    </div>
                                  )}
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    ) : (
                      <div className="flex min-h-[240px] sm:min-h-[300px] items-center justify-center rounded-xl bg-white ">
                        <div className="text-center">
                          <NoItemFoundComponent
                            message=""
                            description=""
                            imageURL={NO_CART}
                            InputClassName="my-0 h-full"
                          />
                          <p className="text-xs font-medium mt-24">
                            {petDetails ? "No vaccination records found." : "No pets found to add vaccination records."}
                          </p>
                          {!petDetails && (
                            <div className="mt-7">
                              <input
                                type="submit"
                                onClick={handleAddPetClick}
                                className="bg-[var(--color-primary)] px-6 py-2 border-transparent rounded-md text-xs text-white cursor-pointer"
                                value={"+ Add Pet"}
                              />
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}
          </div>
        )}
        {page_step === "vaccination_status" && (
          <div className="mt-3 pb-5 sm:pb-0">
            {isVaccinationLoader ? (
              <TableSkeletonComponent columnLength={5} bodyLength={6} />
            ) : (
              <div className="scroll-inner !h-auto max-h-[450px] 2xl:max-h-[550px] flow-root relative rounded-xl bg-[#F9FAFB]">
                <div className="w-full">
                  <div className="inline-block min-w-full align-middle">
                    {Array.isArray(vaccinationSummaryData) && vaccinationSummaryData.length > 0 ? (
                      <table className="w-full min-w-[680px] sm:min-w-[760px] md:min-w-[980px] border-separate border-spacing-x-0 border-spacing-y-0.5 bg-[#F5F5F5] rounded-xl px-[3px] pb-[1px]">
                        <thead className="bg-[#F5F5F5] text-[#1F1F23] font-normal">
                          <tr>
                            <th className="p-2 py-3 text-left text-xs whitespace-nowrap font-medium text-black tracking-wider">
                              Name
                            </th>
                            <th className="p-2 py-3 text-left text-xs whitespace-nowrap font-medium text-black tracking-wider">
                              Last Given Date
                            </th>
                            <th className="p-2 py-3 text-left text-xs whitespace-nowrap font-medium text-black tracking-wider">
                              Next Due Date
                            </th>
                            <th className="p-2 py-3 text-left text-xs whitespace-nowrap font-medium text-black tracking-wider">
                              Next Due Vaccine
                            </th>
                            <th className="p-2 py-3 text-left text-xs whitespace-nowrap font-medium text-black tracking-wider"></th>
                          </tr>
                        </thead>
                        <tbody>
                          {vaccinationSummaryData?.map((vac, index) => {
                            const isFirst = index === 0;
                            const isLast = index === vaccinationSummaryData.length - 1;
                            const isSingle = vaccinationSummaryData.length === 1;
                            return (
                              <tr
                                key={index}
                                className={`
                                  bg-white
                                  ${isSingle ? "rounded-xl" : ""}
                                  ${isFirst && !isSingle ? "rounded-t-xl" : ""}
                                  ${isLast && !isSingle ? "rounded-b-xl" : ""}
                                `}
                              >
                                <td
                                  className={`
                                    px-2 py-3 whitespace-nowrap text-xs font-medium text-black
                                    ${isSingle ? "rounded-tl-2xl rounded-bl-2xl" : ""}
                                    ${isFirst && !isSingle ? "rounded-tl-2xl" : ""}
                                    ${isLast && !isSingle ? "rounded-bl-2xl" : ""}
                                  `}
                                >
                                  {vac?.vaccine_name}
                                </td>
                                <td className="px-2 py-3 whitespace-nowrap text-xs text-black">
                                  {veticDateFormat(vac?.given_date) || "--"}
                                </td>
                                <td className="px-2 py-3 whitespace-nowrap text-xs text-black">
                                  {veticDateFormat(vac?.due_date) || "--"}
                                </td>
                                <td className="px-2 py-3 whitespace-nowrap text-xs text-black">
                                  {vac?.next_due_vaccine_name}
                                </td>
                                <td
                                  className={`
                                    px-2 py-3 whitespace-nowrap text-xs
                                    ${vac?.status === "Overdue" ? "text-red-500 font-medium" : "text-black"}
                                    ${isSingle ? "rounded-tr-2xl rounded-br-2xl" : ""}
                                    ${isFirst && !isSingle ? "rounded-tr-2xl" : ""}
                                    ${isLast && !isSingle ? "rounded-br-2xl" : ""}
                                  `}
                                >
                                  {vac?.status || "NA"}
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    ) : (
                      <div className="flex min-h-[240px] sm:min-h-[300px] items-center justify-center rounded-xl bg-white">
                        <div className="text-center">
                          <NoItemFoundComponent
                            message=""
                            description=""
                            imageURL={NO_CART}
                            InputClassName="my-0 h-full"
                          />
                          <p className="text-xs font-medium mt-24">
                            {petDetails ? "No vaccination records found." : "No pets found to add vaccination records."}
                          </p>
                          {!petDetails && (
                            <div className="mt-7">
                              <input
                                type="submit"
                                onClick={handleAddPetClick}
                                className="bg-[var(--color-primary)] px-6 py-2 border-transparent rounded-md text-xs text-white cursor-pointer"
                                value={"+ Add Pet"}
                              />
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}
          </div>
        )}
        {page_step === "deworming" && <VaccinationDeworming />}
      </div>
      {open && (
        <OCRmodalComponent
          vaccination_id={vaccination_id}
          vaccination_file={vaccination_file}
          mode={mode}
          setMode={setMode}
          vaccinationData={vaccinationProcessingData}
          setVaccinationData={setVaccinationProcessingData}
          open={open}
          setOpen={setOpen}
          loading={loading}
          setLoading={setLoading}
          fileData={fileData}
          setFileData={setFileData}
          vaccinationErrorMessage={vaccinationErrorMessage}
        />
      )}
      {itemDelete && (
        <IteamDeleteComponent
          open={itemDelete}
          setOpen={setItemDelete}
          vaccination_id={vaccination_id}
          vaccination_file={vaccination_file}
        />
      )}
      {fileView && <FileViewComponent open={fileView} setOpen={setFileView} vaccination_file={vaccination_file} />}
    </div>
  );
};

export default VaccinationTab;
